import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { Building2, Mail, User, Shield, Palette, Users, CreditCard, UploadCloud, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "company", label: "Azienda", icon: Building2 },
  { k: "branding", label: "Branding", icon: Palette },
  { k: "team", label: "Team", icon: Users },
  { k: "billing", label: "Piano SaaS", icon: CreditCard },
];

const PLANS = [
  { id: "starter", name: "Starter", price: 49, features: ["1–3 utenti", "Catalogo base", "20 simulazioni/mese", "Supporto email"] },
  { id: "pro", name: "Pro", price: 149, features: ["Utenti illimitati", "Simulazioni illimitate", "Branding PDF", "AI Assistant", "Pagina pubblica"], featured: true },
  { id: "business", name: "Business", price: 399, features: ["Multi-sede", "Listini multipli", "API", "White label", "Supporto prioritario"] },
];

export default function Settings() {
  const { user, company } = useAuth();
  const [tab, setTab] = useState("company");
  const [c, setC] = useState({ ...company });
  const [users, setUsers] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "sales", password: "" });
  const fileRef = React.useRef();
  const sigRef = React.useRef();
  const sigPadRef = React.useRef();
  const sigDrawing = React.useRef(false);

  useEffect(() => { setC({ ...company }); }, [company]);
  useEffect(() => { if (tab === "team") api.get("/users").then((r) => setUsers(r.data)); }, [tab]);

  const saveCompany = async () => {
    try {
      await api.patch("/companies/me", c);
      toast.success("Azienda aggiornata");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) { toast.error("Logo max 600KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setC({ ...c, logo_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const onSignature = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) { toast.error("Firma max 600KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setC((prev) => ({ ...prev, signature_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  // Pad di disegno per la firma aziendale (in alternativa all'upload)
  const sigPos = (e) => {
    const c2 = sigPadRef.current;
    const r = c2.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    return { x: (cx - r.left) * (c2.width / r.width), y: (cy - r.top) * (c2.height / r.height) };
  };
  const sigStart = (e) => {
    e.preventDefault();
    const ctx = sigPadRef.current?.getContext("2d");
    if (!ctx) return;
    sigDrawing.current = true;
    const { x, y } = sigPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const sigMove = (e) => {
    if (!sigDrawing.current) return;
    e.preventDefault();
    const ctx = sigPadRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = sigPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#18181b";
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const sigEnd = (e) => {
    e?.preventDefault?.();
    if (!sigDrawing.current) return;
    sigDrawing.current = false;
    const cv = sigPadRef.current;
    if (cv) setC((prev) => ({ ...prev, signature_base64: cv.toDataURL("image/png") }));
  };
  const sigClearDraw = () => {
    const cv = sigPadRef.current;
    const ctx = cv?.getContext("2d");
    if (ctx && cv) ctx.clearRect(0, 0, cv.width, cv.height);
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Eliminare questo utente dal team?")) return;
    try {
      await api.delete(`/users/${uid}`);
      setUsers((prev) => prev.filter((u) => u.id !== uid));
      toast.success("Utente rimosso");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const sendInvite = async () => {
    try {
      const { data } = await api.post("/auth/invite", invite);
      toast.success(`Utente creato. Password temp: ${data.temp_password}`);
      setInvite({ email: "", name: "", role: "sales", password: "" });
      api.get("/users").then((r) => setUsers(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const subscribe = async (plan) => {
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="page-settings">
      <PageHeader eyebrow="Account" title="Impostazioni" subtitle="Profilo, azienda, team, piano SaaS" />
      <div className="px-8 py-6 max-w-4xl">
        <div className="inline-flex bg-white border border-zinc-200/70 rounded-xl p-1 mb-6 flex-wrap">
          {TABS.map((t) => {
            const Ic = t.icon;
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${active ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`} data-testid={`tab-${t.k}`}>
                <Ic size={14}/> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "company" && (
          <div className="bg-white border border-zinc-200/70 rounded-2xl p-6 space-y-4">
            <div className="text-lg font-semibold flex items-center gap-2"><Building2 size={18} className="text-amber-600"/> Dati azienda</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                {k: "name", l: "Ragione sociale"},
                {k: "vat_number", l: "P.IVA"},
                {k: "email", l: "Email"},
                {k: "phone", l: "Telefono"},
                {k: "address", l: "Indirizzo"},
              ].map((f) => (
                <div key={f.k} className="col-span-2 sm:col-span-1">
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{f.l}</label>
                  <input value={c[f.k] || ""} onChange={(e) => setC({...c, [f.k]: e.target.value})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid={`company-${f.k}`} />
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-100 pt-4">
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-wide mb-2">Costo installazione (default preventivi)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">€ per kWp</label>
                  <input type="number" min="0" step="10" value={c.install_cost_per_kwp ?? ""} placeholder="600"
                    onChange={(e) => setC({ ...c, install_cost_per_kwp: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })}
                    className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono" data-testid="install-per-kwp" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Minimo €</label>
                  <input type="number" min="0" step="100" value={c.install_cost_min ?? ""} placeholder="2500"
                    onChange={(e) => setC({ ...c, install_cost_min: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })}
                    className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono" data-testid="install-min" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5">Voce "Installazione, pratiche, allaccio rete": €/kWp con un minimo. Default 600 €/kWp, min 2.500 €.</p>
            </div>
            <div><div className="text-xs text-zinc-500 font-mono uppercase">Profilo</div>
              <div className="flex items-center gap-3 mt-1">
                <User size={13} className="text-zinc-500"/>{user?.name}
                <Mail size={13} className="text-zinc-500 ml-3"/>{user?.email}
                {user?.email_verified ? <span className="text-emerald-600 text-xs">✓ verificata</span> : <button onClick={async ()=>{await api.post("/auth/send-verification"); toast.success("Link verifica inviato (vedi log demo)");}} className="text-xs text-sky-600 underline" data-testid="verify-email-btn">Verifica</button>}
                <Shield size={13} className="text-zinc-500 ml-3"/><span className="font-mono uppercase text-xs text-emerald-600">{user?.role}</span>
              </div>
            </div>
            <button onClick={saveCompany} className="bg-zinc-900 text-white font-semibold rounded-xl px-5 py-2.5 text-sm" data-testid="save-company-btn">Salva</button>
          </div>
        )}

        {tab === "branding" && (
          <div className="bg-white border border-zinc-200/70 rounded-2xl p-6 space-y-5">
            <div className="text-lg font-semibold flex items-center gap-2"><Palette size={18} className="text-amber-600"/> Branding del preventivo</div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Logo aziendale</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-32 h-20 bg-white rounded-xl flex items-center justify-center overflow-hidden border border-zinc-200/70">
                  {c.logo_base64 ? <img src={c.logo_base64} alt="logo" className="max-h-full max-w-full"/> : <span className="text-xs text-zinc-500">no logo</span>}
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="hidden" data-testid="logo-input" />
                  <button onClick={() => fileRef.current.click()} className="px-4 py-2 border border-zinc-200/70 rounded-lg text-sm flex items-center gap-2" data-testid="logo-upload-btn"><UploadCloud size={14}/> Carica</button>
                  <div className="text-[11px] text-zinc-500 mt-1">PNG/JPG max 600KB · usato sul PDF</div>
                </div>
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Firma aziendale</label>
              <p className="text-[11px] text-zinc-500 mt-1">Disegna la firma qui sotto (mouse o dito), oppure carica un'immagine. Appare nella zona "Firma per l'azienda" del PDF.</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-5">
                <div>
                  <canvas ref={sigPadRef} width={760} height={220}
                    onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
                    onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
                    className="w-[340px] max-w-full h-auto aspect-[760/220] border-2 border-dashed border-zinc-300 rounded-xl bg-zinc-50 touch-none cursor-crosshair block" data-testid="signature-pad" />
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button onClick={sigClearDraw} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="signature-cleardraw-btn">Cancella disegno</button>
                    <span className="text-zinc-300">·</span>
                    <input ref={sigRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onSignature} className="hidden" data-testid="signature-input" />
                    <button onClick={() => sigRef.current.click()} className="text-xs text-amber-600 flex items-center gap-1" data-testid="signature-upload-btn"><UploadCloud size={12}/> Carica immagine</button>
                    {c.signature_base64 && <><span className="text-zinc-300">·</span><button onClick={() => { sigClearDraw(); setC({ ...c, signature_base64: "" }); }} className="text-xs text-rose-500" data-testid="signature-remove-btn">Rimuovi</button></>}
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Anteprima</span>
                  <div className="w-40 h-[92px] bg-white rounded-xl flex items-center justify-center overflow-hidden border border-zinc-200/70 p-2">
                    {c.signature_base64 ? <img src={c.signature_base64} alt="firma azienda" className="max-h-full max-w-full object-contain"/> : <span className="text-xs text-zinc-500">nessuna firma</span>}
                  </div>
                </div>
              </div>
            </div>
            <button onClick={saveCompany} className="bg-zinc-900 text-white font-semibold rounded-xl px-5 py-2.5 text-sm" data-testid="save-branding-btn">Salva branding</button>
          </div>
        )}

        {tab === "team" && (
          <>
            <div className="bg-white border border-zinc-200/70 rounded-2xl p-6 mb-4">
              <div className="text-lg font-semibold mb-3 flex items-center gap-2"><Plus size={18} className="text-emerald-600"/> Invita utente</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <input placeholder="Nome" value={invite.name} onChange={(e) => setInvite({...invite, name: e.target.value})} className="bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid="invite-name"/>
                <input placeholder="email@esempio.it" type="email" value={invite.email} onChange={(e) => setInvite({...invite, email: e.target.value})} className="bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid="invite-email"/>
                <select value={invite.role} onChange={(e) => setInvite({...invite, role: e.target.value})} className="bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid="invite-role">
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="viewer">Viewer</option>
                </select>
                <input placeholder="Password (opz.)" type="text" value={invite.password} onChange={(e) => setInvite({...invite, password: e.target.value})} className="bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid="invite-password"/>
              </div>
              <button onClick={sendInvite} className="mt-3 bg-zinc-900 text-white font-semibold rounded-xl px-5 py-2.5 text-sm" data-testid="invite-submit-btn">Crea utente</button>
            </div>
            <div className="bg-white border border-zinc-200/70 rounded-2xl p-6">
              <div className="text-lg font-semibold mb-3">Team ({users.length})</div>
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200/70 rounded-lg p-3">
                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">{(u.name || "U").slice(0,1).toUpperCase()}</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{u.name}</div>
                      <div className="text-xs text-zinc-500">{u.email}</div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded text-emerald-600 border border-emerald-200">{u.role}</span>
                    {user?.role === "owner" && u.id !== user.id && (
                      <button onClick={() => deleteUser(u.id)} className="ml-1 p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Elimina utente">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === "billing" && (
          <div>
            <div className="text-sm text-zinc-500 mb-4">Piano attivo: <span className="font-mono uppercase text-amber-600">{company?.plan || "starter"}</span></div>
            <div className="grid md:grid-cols-3 gap-3">
              {PLANS.map((p) => {
                const active = (company?.plan || "starter") === p.id;
                return (
                  <div key={p.id} className={`rounded-2xl p-6 border ${p.featured ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200/70"}`}>
                    <div className="font-semibold text-xl">{p.name}</div>
                    <div className={`font-mono text-3xl mt-2 ${p.featured ? "text-amber-600" : "text-zinc-900"}`}>€{p.price}<span className="text-sm text-zinc-500">/mese</span></div>
                    <ul className="mt-4 space-y-2 text-sm text-zinc-900">{p.features.map((f) => <li key={f} className="flex items-center gap-2"><Check size={13} className="text-emerald-600"/>{f}</li>)}</ul>
                    <button onClick={() => !active && subscribe(p.id)} disabled={active} className={`mt-5 w-full rounded-xl py-2.5 text-sm font-semibold ${active ? "bg-zinc-100 text-zinc-500" : p.featured ? "bg-zinc-900 text-white hover:bg-zinc-800" : "bg-zinc-100 text-zinc-900 hover:bg-zinc-200"}`} data-testid={`subscribe-${p.id}`}>
                      {active ? "Piano attivo" : "Sottoscrivi"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
